import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { WarehouseConnectionRule } from '@domain/inventory/models/WarehouseConnectionRule';


export const useWarehouseConnection = (warehouseId?: string) => {
    const queryClient = useQueryClient();

    const {
        data: connectionsResponse,
        isLoading: isLoadingConnections
    } = useQuery({
        queryKey: ['warehouse-connections', warehouseId],
        queryFn: async () => {
            if (!warehouseId) return null;
            const result = await container.warehouseConnectionRepository.getConnections(warehouseId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!warehouseId
    });

    const { mutateAsync: createConnection } = useMutation({
        mutationFn: async (data: { warehouseId: string, rules: WarehouseConnectionRule[], mutationType: MutationType, activeFrom?: Date, activeUntil?: Date, shouldCreateMutations?: boolean }) => {
            const result = await container.warehouseConnectionRepository.createConnection(
                data.warehouseId,
                data.rules,
                data.mutationType,
                data.activeFrom,
                data.activeUntil,
                data.shouldCreateMutations
            );
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['warehouse-connections', warehouseId]);
        }
    });

    const { mutateAsync: deleteConnection } = useMutation({
        mutationFn: async (id: string) => {
            // Get the connection first to check its type
            const connections = await container.warehouseConnectionRepository.getConnections(warehouseId || '');
            if (connections.status === 'error') throw new Error(connections.error.message);

            const connection = connections.data.find(c => c.id === id);
            if (!connection) throw new Error('Connection not found');

            if (connection.mutationType !== MutationType.INCOMING && connection.mutationType !== MutationType.OUTGOING) {
                throw new Error('Only incoming and outgoing connections can be deleted');
            }

            const result = await container.warehouseConnectionRepository.deleteConnection(id);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['warehouse-connections', warehouseId]);
        }
    });

    const { mutateAsync: updateConnection } = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: { rules: WarehouseConnectionRule[], mutationType: MutationType, activeFrom?: Date, activeUntil?: Date } }) => {
            // Get the connection first to check its type
            const connections = await container.warehouseConnectionRepository.getConnections(warehouseId || '');
            if (connections.status === 'error') throw new Error(connections.error.message);

            const connection = connections.data.find(c => c.id === id);
            if (!connection) throw new Error('Connection not found');

            if (connection.mutationType !== MutationType.INCOMING && connection.mutationType !== MutationType.OUTGOING) {
                throw new Error('Only incoming and outgoing connections can be updated');
            }

            const result = await container.warehouseConnectionRepository.updateConnection(id, data.rules, data.mutationType, data.activeFrom, data.activeUntil);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['warehouse-connections', warehouseId]);
        }
    });

    return {
        warehouseConnections: connectionsResponse || [],
        isLoading: isLoadingConnections,
        createConnection,
        deleteConnection,
        updateConnection
    };
}; 
